import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { useRevenueAnalytics, RevenueItem } from '../use-cases/useRevenueAnalytics';
import RevenueLockGate from './RevenueLockGate';
import RetentionPanel from './RetentionPanel';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('ko-KR').format(amount || 0) + '원';

const formatMonthLabel = (month: string) => {
  const [, m] = month.split('-');
  return `${Number(m)}월`;
};

const formatMonthTitle = (month: string) => {
  const [y, m] = month.split('-');
  return `${y}년 ${Number(m)}월`;
};

const formatDate = (dateStr: string | null) => (dateStr ? dateStr.slice(0, 10) : '-');

const paymentMethodLabel = (method: string) => {
  const labels: Record<string, string> = {
    card: '카드',
    cash: '현금',
    transfer: '계좌이체',
    기타: '기타',
  };
  return labels[method] || method || '기타';
};

const growthLabel = (rate: number | null) => {
  if (rate === null || rate === undefined) return null;
  const sign = rate >= 0 ? '+' : '';
  return `${sign}${rate.toFixed(1)}%`;
};

const downloadCsv = (month: string, basis: string, items: RevenueItem[]) => {
  const accrual = basis === 'accrual';
  const header = [
    '결제일',
    '회원',
    '이용권',
    '구분',
    '상태',
    '정가',
    '할인',
    '결제액',
    ...(accrual ? ['이 달 인식액'] : []),
    '시작일',
    '만료일',
    '결제수단',
  ];

  const rows = items.map((it) =>
    [
      formatDate(it.paid_on),
      it.user_name,
      it.name,
      it.is_new ? '신규' : '재등록',
      it.status === 'cancelled' ? '취소' : '정상',
      it.original_price ?? '',
      it.discount_amount ?? 0,
      it.price,
      ...(accrual ? [it.month_amount] : []),
      formatDate(it.start_date),
      formatDate(it.end_date),
      paymentMethodLabel(it.payment_method),
    ]
      .map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`)
      .join(',')
  );

  // 엑셀이 UTF-8 로 열도록 BOM 을 붙인다
  const blob = new Blob(['﻿' + [header.join(','), ...rows].join('\n')], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `매출_${month}_${basis === 'accrual' ? '이용기준' : '입금기준'}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

function SummaryCard({
  label,
  value,
  sub,
  tone = 'default',
}: {
  label: string;
  value: string;
  sub?: React.ReactNode;
  tone?: 'default' | 'green' | 'red' | 'purple';
}) {
  const toneClass = {
    default: 'text-gray-900',
    green: 'text-green-600',
    red: 'text-red-600',
    purple: 'text-purple-600',
  }[tone];
  return (
    <div className="bg-white p-5 rounded-lg shadow">
      <p className="text-sm text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${toneClass}`}>{value}</p>
      {sub && <div className="mt-1 text-xs text-gray-500">{sub}</div>}
    </div>
  );
}

function RevenueAnalyticsView() {
  const {
    month,
    basis,
    setBasis,
    data,
    loading,
    error,
    isCurrentMonth,
    goPrevMonth,
    goNextMonth,
    refresh,
  } = useRevenueAnalytics();

  const summary = data?.summary;
  const items = data?.items ?? [];

  const chartData = useMemo(
    () =>
      (data?.trend ?? []).map((t) => ({
        month: formatMonthLabel(t.month),
        신규: t.new_amount,
        재등록: t.renew_amount,
        건수: t.count,
      })),
    [data?.trend]
  );

  const methodTotal = (data?.payment_methods ?? []).reduce((s, p) => s + p.total_amount, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <CurrencyDollarIcon className="h-7 w-7 text-blue-600 mr-2" />
              매출
            </h1>

            <div className="flex items-center gap-2">
              {/* 월 선택 */}
              <div className="flex items-center bg-gray-100 rounded-lg">
                <button
                  onClick={goPrevMonth}
                  className="p-2 hover:bg-gray-200 rounded-l-lg"
                  aria-label="이전 달"
                >
                  <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
                </button>
                <span className="px-3 text-sm font-semibold text-gray-900 min-w-[110px] text-center">
                  {formatMonthTitle(month)}
                </span>
                <button
                  onClick={goNextMonth}
                  disabled={isCurrentMonth}
                  className="p-2 hover:bg-gray-200 rounded-r-lg disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="다음 달"
                >
                  <ChevronRightIcon className="h-5 w-5 text-gray-600" />
                </button>
              </div>

              {/* 기준 토글 */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                {(['cash', 'accrual'] as const).map((b) => (
                  <button
                    key={b}
                    onClick={() => setBasis(b)}
                    className={`px-3 py-1.5 text-sm rounded-md transition ${
                      basis === b
                        ? 'bg-white shadow font-semibold text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {b === 'cash' ? '입금 기준' : '이용 기준'}
                  </button>
                ))}
              </div>

              <button
                onClick={refresh}
                className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200"
                aria-label="새로고침"
              >
                <ArrowPathIcon className={`h-5 w-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
              </button>

              <button
                onClick={() => downloadCsv(month, basis, items)}
                disabled={items.length === 0}
                className="flex items-center gap-1 px-3 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                CSV
              </button>
            </div>
          </div>

          <p className="mt-2 text-sm text-gray-500">
            {basis === 'cash'
              ? '입금 기준 — 그 달에 실제로 받은 돈. 통장·카드정산·부가세 신고와 대조하는 숫자입니다.'
              : '이용 기준 — 이용기간에 맞춰 일할 안분. 선결제가 한 달에 몰리지 않아 매장 규모를 봅니다.'}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {loading && !data ? (
          <div className="py-24 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
            <p className="mt-4 text-gray-600">매출 데이터 로딩 중...</p>
          </div>
        ) : summary ? (
          <>
            {/* 요약 카드 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <SummaryCard
                label={`${formatMonthTitle(month)} 매출`}
                value={formatCurrency(summary.revenue)}
                sub={
                  summary.growth_rate !== null ? (
                    <span className={summary.growth_rate >= 0 ? 'text-green-600' : 'text-red-600'}>
                      전월 대비 {growthLabel(summary.growth_rate)} (
                      {formatCurrency(summary.prev_revenue)})
                    </span>
                  ) : (
                    <span>전월 {formatCurrency(summary.prev_revenue)}</span>
                  )
                }
              />
              <SummaryCard
                label="신규"
                value={formatCurrency(summary.new_amount)}
                sub={`${summary.new_count}건`}
                tone="green"
              />
              <SummaryCard
                label="재등록"
                value={formatCurrency(summary.renew_amount)}
                sub={`${summary.renew_count}건`}
                tone="purple"
              />
              <SummaryCard
                label="결제 인원"
                value={`${summary.payer_count}명`}
                sub={`1인 평균 ${formatCurrency(summary.avg_per_payer)} · 총 ${summary.count}건`}
              />
              <SummaryCard
                label="할인 / 취소"
                value={formatCurrency(summary.discount_total)}
                sub={
                  summary.cancelled_count > 0 ? (
                    <span className="text-red-600">
                      취소 {summary.cancelled_count}건 {formatCurrency(summary.cancelled_amount)}
                    </span>
                  ) : (
                    '취소 없음'
                  )
                }
                tone="red"
              />
            </div>

            {/* 선수금 안내 */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
              <b>선수금 잔액 {formatCurrency(summary.deferred_balance)}</b> — 이미 받았지만 아직
              제공하지 않은 이용기간에 해당하는 금액입니다. 환불 요청이 몰릴 때 돌려줘야 할 규모로
              보면 됩니다.
            </div>

            {/* 갱신율 · 이탈 · 가동률 — 매출 기준과 무관해서 토글해도 다시 안 부른다 */}
            <RetentionPanel month={month} />

            {/* 추이 + 결제수단 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white p-5 rounded-lg shadow">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  최근 12개월 추이 ({basis === 'cash' ? '입금' : '이용'} 기준)
                </h3>
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(v) => `${(v / 10000).toFixed(0)}만`} />
                      <Tooltip formatter={(value: any) => formatCurrency(Number(value))} />
                      <Legend />
                      <Bar dataKey="신규" stackId="a" fill="#10B981" />
                      <Bar dataKey="재등록" stackId="a" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-gray-400 text-center py-12">매출 데이터가 없습니다</p>
                )}
              </div>

              <div className="bg-white p-5 rounded-lg shadow">
                <h3 className="text-lg font-medium text-gray-900 mb-1">결제수단별</h3>
                <p className="text-xs text-gray-400 mb-4">실제 입금 기준 (기준 토글과 무관)</p>
                {(data?.payment_methods.length ?? 0) > 0 ? (
                  <div className="space-y-4">
                    {data!.payment_methods.map((pm) => {
                      const pct = methodTotal > 0 ? (pm.total_amount / methodTotal) * 100 : 0;
                      return (
                        <div key={pm.method}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">{paymentMethodLabel(pm.method)}</span>
                            <span className="font-medium">
                              {formatCurrency(pm.total_amount)} ({pm.count}건)
                            </span>
                          </div>
                          <div className="bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-500 h-2 rounded-full"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-400 text-center py-8">데이터 없음</p>
                )}
              </div>
            </div>

            {/* 할인 사유별 */}
            <div className="bg-white rounded-lg shadow p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">💸 할인 사유별</h3>
                <span className="text-sm text-gray-600">
                  이 달 할인 총액{' '}
                  <b className="text-red-600">{formatCurrency(summary.discount_total)}</b>
                </span>
              </div>
              {(data?.discount_by_reason.length ?? 0) > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          사유
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                          건수
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                          할인 총액
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                          건당 평균
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data!.discount_by_reason.map((d) => (
                        <tr key={d.reason} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm font-medium text-gray-900">{d.reason}</td>
                          <td className="px-4 py-2 text-sm text-right text-gray-600">{d.count}건</td>
                          <td className="px-4 py-2 text-sm text-right font-semibold text-red-600">
                            {formatCurrency(d.total_discount)}
                          </td>
                          <td className="px-4 py-2 text-sm text-right text-gray-500">
                            {formatCurrency(Math.round(d.total_discount / Math.max(1, d.count)))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-400 text-center py-6">이 달 할인 내역이 없습니다.</p>
              )}
            </div>

            {/* 그 달 결제 내역 */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  {formatMonthTitle(month)} 내역
                </h3>
                <p className="text-sm text-gray-500">
                  총 {items.length}건
                  {basis === 'accrual' && ' — 이 달 이용기간이 걸친 이용권 전부'}
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        결제일
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        회원
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        이용권
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        이용기간
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        할인
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        결제액
                      </th>
                      {basis === 'accrual' && (
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                          이 달 인식액
                        </th>
                      )}
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        결제수단
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {items.length > 0 ? (
                      items.map((it) => {
                        const cancelled = it.status === 'cancelled';
                        return (
                          <tr
                            key={it.id}
                            className={`hover:bg-gray-50 ${cancelled ? 'bg-red-50/40 text-gray-400' : ''}`}
                          >
                            <td className="px-4 py-3 text-sm">{formatDate(it.paid_on)}</td>
                            <td className="px-4 py-3 text-sm font-medium">
                              <span className={cancelled ? 'line-through' : 'text-gray-900'}>
                                {it.user_name || '-'}
                              </span>
                              <span
                                className={`ml-2 px-1.5 py-0.5 rounded text-xs font-medium ${
                                  it.is_new
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-purple-100 text-purple-700'
                                }`}
                              >
                                {it.is_new ? '신규' : '재등록'}
                              </span>
                              {cancelled && (
                                <span className="ml-1 px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                                  취소
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm">{it.name}</td>
                            <td className="px-4 py-3 text-sm">
                              {formatDate(it.start_date)} ~ {formatDate(it.end_date)}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-red-500">
                              {it.discount_amount > 0 ? formatCurrency(it.discount_amount) : '-'}
                            </td>
                            <td
                              className={`px-4 py-3 text-sm text-right font-medium ${cancelled ? 'line-through' : ''}`}
                            >
                              {formatCurrency(it.price)}
                            </td>
                            {basis === 'accrual' && (
                              <td className="px-4 py-3 text-sm text-right font-semibold text-blue-600">
                                {cancelled ? '-' : formatCurrency(it.month_amount)}
                              </td>
                            )}
                            <td className="px-4 py-3 text-sm text-center">
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                {paymentMethodLabel(it.payment_method)}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={basis === 'accrual' ? 8 : 7}
                          className="px-4 py-8 text-center text-gray-400"
                        >
                          이 달 내역이 없습니다
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

// 매출 비밀번호 잠금으로 감싼 기본 export — 잠금 통과 전엔 매출 데이터를 불러오지 않음
export default function RevenueAnalyticsPage() {
  return (
    <RevenueLockGate>
      <RevenueAnalyticsView />
    </RevenueLockGate>
  );
}
